import { AuthService } from '@/app/core/services/auth/auth.service';
import { DebugableService } from '@/app/core/services/debug/debug.type';
import { environment } from '@/environments/environment';
import {
  GetCardAutomationsForCacheGQL,
  GetCardAutomationsForCacheQuery,
  GetCardAutomationsGQL,
  GetCardAutomationsQuery,
  StageAutomationExecutionsActionsStatus,
} from '@/generated/graphql';
import { Injectable, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { safeJsonParse } from '@core/utils/safe-json-parse';
import { AutomationSSEEvent } from '@flux/core/client';
import { firstValueFrom, interval, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class BoardAutomationDisplayService implements DebugableService, OnDestroy {
  public currentCardId = signal<string | null>(null);

  public cardAutomations = signal<GetCardAutomationsQuery['card'] | null>(null);
  public cardAutomationsState = signal<'loading' | 'success' | 'error'>('loading');

  public isDisplaying = signal<boolean>(false);

  private responseSSE: Response | null = null;

  public automationChanged = new Subject<{
    cardId: string;
    isLocked: boolean;
    automation: { status: StageAutomationExecutionsActionsStatus }[];
  }>();

  private abortController = new AbortController();

  public cardsAutomationCache = new Map<
    string,
    {
      status: StageAutomationExecutionsActionsStatus;
    }
  >();

  constructor(
    private readonly getCardAutomationsGQL: GetCardAutomationsGQL,
    private readonly getCardAutomationsForCacheGQL: GetCardAutomationsForCacheGQL,
    private readonly authService: AuthService,
  ) {
    toObservable(this.cardAutomations)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        const cardAutomations = this.cardAutomations();

        if (!cardAutomations) {
          return;
        }

        this.automationChanged.next({
          cardId: this.currentCardId()!,
          isLocked: cardAutomations.automations.some(
            (automation) => automation.automation.isSync && automation.status === 'pending',
          ),
          automation: cardAutomations.automations,
        });
      });

    interval(1000 * 60)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        const pendingAutomations = Array.from(this.cardsAutomationCache.entries()).filter(
          ([, { status }]) => status === 'pending',
        );

        for (const [cardId] of pendingAutomations) {
          this.fetchCardAutomationsForCache(cardId);
        }
      });
  }

  public ngOnDestroy(): void {
    this.closeSSE();
    this.automationChanged.complete();
  }

  public setCurrentCardId(cardId: string): void {
    this.closeSSE();
    this.currentCardId.set(cardId);
    this.fetchCardAutomationsForBoard();
  }

  public async fetchCardAutomationsForCache(cardId: string): Promise<void> {
    try {
      const result = await firstValueFrom(this.getCardAutomationsForCacheGQL.fetch({ cardId }));

      if (result.errors) {
        console.error('Error fetching card automations:', result.errors);
        return;
      }

      this.resolveAndUpdateCache(cardId, result.data.card.automations);
    } catch (error) {
      console.error('Error fetching card automations:', error);
    }
  }

  public async fetchCardAutomationsForBoard(): Promise<void> {
    const cardId = this.currentCardId();

    if (!cardId) {
      console.warn('No card ID to fetch automations for');
      return;
    }

    this.cardAutomationsState.set('loading');
    try {
      const result = await firstValueFrom(this.getCardAutomationsGQL.fetch({ cardId }));

      if (result.errors) {
        console.error('Error fetching card automations:', result.errors);
        this.cardAutomationsState.set('error');
        return;
      }

      if (result.data.card.automations.length === 0) {
        this.cardAutomationsState.set('success');

        this.cardAutomations.set(null);
        this.currentCardId.set(null);
        this.isDisplaying.set(false);

        this.automationChanged.next({ cardId, isLocked: false, automation: [] });
        return;
      }

      this.cardAutomations.set(result.data.card);

      this.resolveAndUpdateCache(cardId, result.data.card.automations);

      if (result.data.card.automations.some((automation) => automation.status === 'pending')) {
        this.subscribeToSSE();
      }

      this.isDisplaying.set(true);
      this.cardAutomationsState.set('success');
    } catch (error) {
      console.error('Error fetching card automations:', error);
      this.cardAutomationsState.set('error');
    }
  }

  public async subscribeToSSE(): Promise<void> {
    if (this.responseSSE && !this.responseSSE.body?.locked) {
      this.responseSSE.body?.cancel();
    }

    if (!this.authService.authenticatedUser) {
      console.warn('User is not authenticated');
      return;
    }

    if (!this.currentCardId()) {
      console.warn('No card ID to subscribe to');
      return;
    }

    const cardId = this.currentCardId();

    const baseUrl = new URL(environment.ISENGARD_URL);
    baseUrl.pathname = `/core/sync-automation/${cardId}/sse`;

    this.abortController = new AbortController();

    try {
      if (!this.authService.authenticatedUser()?.token) {
        console.warn('[SSE]No token to subscribe to SSE');
        return;
      }

      this.responseSSE = await fetch(baseUrl.toString(), {
        method: 'GET',
        signal: this.abortController.signal,
        headers: {
          Authorization: this.authService.authenticatedUser()?.token ?? '',
        },
      });

      const reader = this.responseSSE.body?.getReader();
      const decoder = new TextDecoder('utf-8');

      if (!reader) {
        console.error('Failed to get reader from response body');
        return;
      }

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader
          .read()
          .catch(() => ({ value: null, done: true }) as const);
        if (done) break;

        const decodedValue = decoder.decode(value);
        const [eventLine, idLine, dataLine] = decodedValue.split('\n') as [string, string, string];

        console.log('[SSE]: Event received', { id: idLine });

        if (!eventLine.startsWith('event: message')) {
          continue;
        }

        if (!idLine.startsWith('id: ')) {
          continue;
        }

        const data = safeJsonParse<AutomationSSEEvent>(dataLine.split('data: ')[1]);

        if (!data || !data.records || !Array.isArray(data.records)) {
          console.warn('Invalid data received from SSE:', data);
          continue;
        }

        this.resolveAndUpdateCache(
          cardId!,
          data.records as GetCardAutomationsQuery['card']['automations'],
        );

        this.cardAutomations.update((prev) => {
          if (!prev) {
            return prev;
          }

          return {
            ...prev,
            automations: data.records as GetCardAutomationsQuery['card']['automations'],
          };
        });
      }
    } catch (error) {
      console.error('Error subscribing to SSE:', error);
    }

    if (this.responseSSE?.body?.locked || this.abortController.signal.aborted) {
      return;
    }

    this.closeSSE();
  }

  public manualClose(): void {
    this.closeSSE();
    this.isDisplaying.set(false);
  }

  public closeSSE(): void {
    if (!this.abortController.signal.aborted) {
      this.abortController.abort();
    }

    if (this.responseSSE && !this.responseSSE.body?.locked) {
      this.responseSSE.body?.cancel();
    }

    this.responseSSE = null;
    this.cardAutomations.set(null);
    this.currentCardId.set(null);
    this.cardAutomationsState.set('loading');
  }

  public resolveAndUpdateCache(
    cardId: string,
    automations: GetCardAutomationsForCacheQuery['card']['automations'],
  ): void {
    if (!automations.length) {
      this.cardsAutomationCache.delete(cardId);
      this.automationChanged.next({ cardId, isLocked: false, automation: [] });
      return;
    }

    const status = (
      automations.some((automation) => automation.status === 'pending')
        ? 'pending'
        : automations.every((automation) => automation.status === 'done')
          ? 'done'
          : 'error'
    ) as StageAutomationExecutionsActionsStatus;

    if (status === 'pending') {
      this.cardsAutomationCache.set(cardId, { status });
    } else {
      this.cardsAutomationCache.delete(cardId);
    }

    this.automationChanged.next({
      cardId,
      isLocked: automations.some(
        (automation) => automation.automation.isSync && automation.status === 'pending',
      ),
      automation: automations,
    });
  }

  public generateDebugReport() {
    return {
      cardId: this.currentCardId(),
      cardAutomations: this.cardAutomations(),
      cardAutomationsState: this.cardAutomationsState(),
      isDisplaying: this.isDisplaying(),
      responseSSE: this.responseSSE,
      cardsAutomationCache: Object.fromEntries(this.cardsAutomationCache),
    };
  }
}
